<template>
	<div :class="rootClass">
		<strong class="tit_view"><em class="txt_point">추천</em> 인기글</strong>
		<ul class="list_hotview" id="list_hotview" ref="articleElementsWrapper">
			<card-item v-for="(article, index) in renderedArticles" :article="article" :feedback="feedback" :order-num="index + 1" :config="config" ref="articleElements"></card-item>
		</ul>
		<div class="wrap_btn">
			<button type="button" class="btn_item btn_moreview" @click="renderMoreArticles" v-if="renderedArticleCount < articles.length">더보기</button>
		</div>
	</div>
</template>

<script>
import CardItem from './CardItem.vue';
import adfitNativeAds from '@src/native-ad.js';
import { addVimps } from '../tesla-queue.js';

const VIEW_IMP_DEBOUNCE_DELAY = 1000;

export default {
	components: {
		CardItem
	},
	props: {
		config: Object,
		articles: {
			type: Array,
			default: () => ([])
		},
		feedback: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			MORE_VIEW_SIZE: 10,
			renderedArticleCount: 10,

			MAX_AD_COUNT: 10,
			AD_COUNT_PER_PAGE: 2,
			ads: [],
			page: 0
		};
	},
	computed: {
		rootClass() {
			return {
				'view_card': true,
				'view_hotview': true,
				'view_card--no_contents': this.articles.length === 0
			};
		},
		renderedArticles() {
			return this.newArticles.slice(0, this.renderedArticleCount);
		},
		localStorageSupport() {
			let support = typeof localStorage !== 'undefined';
			try {
				localStorage.setItem('_', '');
			} catch (e) {
				support = false;
			}
			return support;
		},
		skipCount() {
			if (document.referrer !== '' || !this.localStorageSupport) {
				return 0;
			}
			let index = localStorage.getItem('nextSkip') || 0;
			index = parseInt(index);
			if (index === 0 || index > this.articles.length) {
				localStorage.setItem('nextSkip', '6');
				return 0;
			}
			localStorage.setItem('nextSkip', (index + 6).toString());
			return index;
		},
		newArticles() {
			let newArticles = [...this.articles];
			const skippedArticles = newArticles.splice(0, this.skipCount);
			return [...newArticles, ...skippedArticles];
		}
	},
	methods: {
		renderMoreArticles() {
			this.$trackEvent({
				layer: { layer1: 'recommend_hot_article_more'},
				layerName: "추천인기글더보기",
			});
			this.renderedArticleCount += this.MORE_VIEW_SIZE;
			if (this.renderedArticleCount > this.newArticles.length) {
				this.renderedArticleCount = this.newArticles.length;
			}

			this.$nextTick(() => {
				this.appendAdfitNativeAds();
				this._observeCards();
			});
		},
		fetchAdfitNativeAds() {
			adfitNativeAds(this.config.svcParam, this.MAX_AD_COUNT, ads => {
				this.ads = ads;
				this.appendAdfitNativeAds();
			}).init();
		},
		appendAdfitNativeAds() {
			const adPositionMap = {
				0: 2,
				1: 6
			}

			this.ads.splice(0, this.AD_COUNT_PER_PAGE).forEach((ad, idx) => {
				const position = this.page * this.MORE_VIEW_SIZE + adPositionMap[idx];
				const content = this.$refs.articleElements[position]?.$el;

				if (content) {
					this.$refs.articleElementsWrapper.insertBefore(ad, content);
				}
			});

			this.page++;
		},
		_setupViewImpTracking() {
			if (typeof IntersectionObserver === 'undefined') return;

			this._pendingMap = new Map();
			this._sentIds = new Set();
			this._flushTimer = null;
			this._observer = new IntersectionObserver(entries => this._collectViewImpEntries(entries), { threshold: 1 });

			//bfcache문제로 beforeunload -> pagehide/pageshow 이벤트로 변경
			this._onPageHide = () => this._flushViewImp();
			this._onPageShow = (e) => {
				if (e.persisted) { // 브라우저 bfcache에서 복원된 경우
					this._clearFlushTimer();
					this._pendingMap.clear();
					this._sentIds.clear();
					this._observeCards();
				}
			};
			this._onScroll = () => {
				this._drainObserverRecords();
				if (this._pendingMap.size) this._scheduleFlushViewImp();
			};
			window.addEventListener('scroll', this._onScroll, { passive: true });
			window.addEventListener('pagehide', this._onPageHide);
			window.addEventListener('pageshow', this._onPageShow);

			this._observeCards();
		},
		_collectViewImpEntries(entries) {
			const newItemIds = []; // tesla 본거빼기 기능용: 새로 보인 item만 queue에 반영하기 위한 입력
			entries.forEach(entry => {
				if (!entry.isIntersecting || entry.intersectionRatio < 1) return;

				const itemId = entry.target.dataset.tiaraContId;
				if (!itemId || this._pendingMap.has(itemId) || this._sentIds.has(itemId)) {
					this._observer.unobserve(entry.target);
					return;
				}

				this._observer.unobserve(entry.target);
				newItemIds.push(itemId);
				this._pendingMap.set(itemId, {
					id: itemId,
					layer1: 'recommend_hot_article',
					imp_id: entry.target.dataset.tiaraImpId || '',
					imp_ordnum: entry.target.dataset.tiaraImpOrdnum,
					imp_extra_info: entry.target.dataset.tiaraFeedback
				});
			});

			if (newItemIds.length) {
				addVimps(newItemIds); // localStorage 저장
				this._scheduleFlushViewImp();
			}
		},
		_drainObserverRecords() {
			if (!this._observer || typeof this._observer.takeRecords !== 'function') return;

			const records = this._observer.takeRecords();
			if (records.length) this._collectViewImpEntries(records);
		},
		_scheduleFlushViewImp() {
			this._clearFlushTimer();
			if (!this._pendingMap.size) return;
			this._flushTimer = setTimeout(() => this._flushViewImp(), VIEW_IMP_DEBOUNCE_DELAY);
		},
		_clearFlushTimer() {
			if (!this._flushTimer) return;
			clearTimeout(this._flushTimer);
			this._flushTimer = null;
		},
		_flushViewImp() { // 실제 viewImpression 이벤트 전송
			this._clearFlushTimer();
			if (!this._pendingMap || !this._pendingMap.size) return;

			const contents = [...this._pendingMap.values()];
			this.$trackViewImp({
				page: 'hot_article_view',
				layerName: "추천인기글카드",
				contents,
				customProps: this.feedback || {}
			});
			contents.forEach(({ id }) => this._sentIds.add(id));
			this._pendingMap.clear();
		},
		_observeCards() {
			if (!this._observer) return;
			const wrapper = this.$refs.articleElementsWrapper;
			if (!wrapper) return;
			//광고카드 제외, 인기글카드만
			wrapper.querySelectorAll('li:not([adfit-unit-id]) .hotview_wrap').forEach(el => {
				const itemId = el.dataset.tiaraContId;
				if (!itemId || this._pendingMap.has(itemId) || this._sentIds.has(itemId)) return;
				this._observer.observe(el);
			});
		}
	},
	mounted() {
		// 광고 차단 여부와 관계없이 인기글 카드 노출 추적을 먼저 초기화한다.
		this._setupViewImpTracking();
		try {
			this.fetchAdfitNativeAds();
		} catch (e) {
			//광고 실패가 인기글 카드 노출 수집에 영향을 주지 않도록 처리.
			console.log(e);
		}
	},
	beforeDestroy() {
		this._flushViewImp();
		if (this._observer) this._observer.disconnect();
		if (this._onPageHide) window.removeEventListener('pagehide', this._onPageHide);
		if (this._onPageShow) window.removeEventListener('pageshow', this._onPageShow);
		if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
	}
}
</script>
